<template>
  <DBForm :store="store" :config="settings" :data="data" ref="form">
    <template v-slot="form">
      <Card :panelFun="controller.panelFun.value" :panel="panel.key">
        <div v-if="form.data?.status === 'posted'" class="alert alert-info py-2 small mb-2">
          Документ проведён: движения по складу сформированы. Позиции табличной части недоступны для
          изменения. Для исправления переведите статус в «Черновик», сохраните, затем снова проведите.
        </div>

        <div class="card mb-2">
          <div class="card-header bg-light py-1">
            <h6 class="mb-0">Акт списания медикаментов и перевязочных средств</h6>
          </div>
          <div class="card-body py-2">
            <div class="form-row">
              <div class="col-lg-3 my-input-1">
                <DBEdit :form="form" field="act_number" clear/>
              </div>
              <div class="col-lg-2 date-input">
                <DBEdit :form="form" field="act_date" clear/>
              </div>
              <div class="col-lg-3">
                <DBEdit :form="form" field="status" clear />
              </div>
            </div>
            <div class="form-row">
              <div class="col-lg-6">
                <DBEdit :form="form" field="warehouse_id" />
              </div>
              <div class="col-lg-6">
                <DBEdit :form="form" field="department_id" />
              </div>
            </div>
            <div class="form-row">
              <div class="col-lg-6">
                <DBEdit :form="form" field="responsible_position" clear />
              </div>
              <div class="col-lg-6">
                <DBEdit :form="form" field="responsible_fio" clear />
              </div>
            </div>
            <div class="form-row">
              <div class="col-lg-12">
                <DBEdit :form="form" field="commission_text" clear />
              </div>
            </div>
            <div class="form-row">
              <div class="col-lg-3 date-input">
                <DBEdit :form="form" field="order_date" clear />
              </div>
              <div class="col-lg-3">
                <DBEdit :form="form" field="order_number" clear />
              </div>
            </div>
          </div>
        </div>

        <div class="card mb-2">
          <div class="card-header bg-light py-1">
            <h6 class="mb-0">Комиссия: подписи (должность, ФИО)</h6>
          </div>
          <div class="card-body py-2">
            <div class="form-row">
              <div class="col-lg-6">
                <DBEdit :form="form" field="chair_position" clear />
              </div>
              <div class="col-lg-6">
                <DBEdit :form="form" field="chair_fio" clear />
              </div>
            </div>
            <div class="form-row">
              <div class="col-lg-6">
                <DBEdit :form="form" field="member1_position" clear />
              </div>
              <div class="col-lg-6">
                <DBEdit :form="form" field="member1_fio" clear />
              </div>
            </div>
            <div class="form-row">
              <div class="col-lg-6">
                <DBEdit :form="form" field="member2_position" clear />
              </div>
              <div class="col-lg-6">
                <DBEdit :form="form" field="member2_fio" clear />
              </div>
            </div>
          </div>
        </div>

        <div class="card mb-2">
          <div class="card-header bg-light py-1">
            <h6 class="mb-0">Списываемые медикаменты и перевязочные средства</h6>
          </div>
          <div class="card-body py-2">
            <DBGrid
              :form="form"
              field="items"
              :config="settings.fields.items"
              :owner="form.data?.id"
              :readonly="form.data?.status === 'posted'"
              ref="itemsGrid"
            />
          </div>
        </div>

        <div class="card mb-2">
          <div class="card-header bg-light py-1">
            <h6 class="mb-0">Итоги и основания</h6>
          </div>
          <div class="card-body py-2 small">
            <p class="mb-1">
              <strong>Всего по акту списания, количество:</strong>
              {{ totalQty }} (сумма натуральных единиц по строкам: кол-во уп. × кол-во в уп. по М-17).
              <span v-if="qtyWords"><em>{{ qtyWords }}</em></span>
            </p>
            <p class="mb-1">
              <strong>Всего по акту списания, сумма:</strong>
              {{ totalAmt.toFixed(2) }} руб.
            </p>
            <div class="form-row">
              <div class="col-lg-12">
                <DBEdit :form="form" field="funding_sources_note" />
              </div>
            </div>
            <hr />
            <p class="mb-1 text-muted">
              Материально-ответственное лицо: {{ footerMol }}
            </p>
            <p class="mb-0 text-muted">Комиссия: {{ footerCommission }}</p>
          </div>
        </div>
      </Card>
    </template>
  </DBForm>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from "vue";
import MedicamentWriteoffActsEditController from "./EditController";
import Swal from "sweetalert2";

export default defineComponent({
  props: {
    panel: { type: Object, default: () => ({}) },
    store: { type: Object, default: () => ({}) },
    config: { type: Object, default: () => ({}) },
    data: { type: Object, default: () => ({}) },
  },
  setup(props) {
    const form = ref(null);
    const itemsGrid = ref(null);
    const tick = ref(0);
    const controller = new MedicamentWriteoffActsEditController(form, props);

    const originalSave = controller.standardCommands.save.onClick;
    controller.standardCommands.save.onClick = async () => {
      const d = (form.value as any)?.data;
      if (d?.status === "posted") {
        const r = await Swal.fire({
          title: "Провести акт?",
          text: "Будут созданы движения расхода по карточкам М-17 и пересчитаны остатки.",
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Да, провести",
          cancelButtonText: "Отмена",
        });
        if (!r.isConfirmed) return;
      }
      await originalSave();
    };

    const settings = controller.settings();

    watch(
      () => (form.value as any)?.data?.items,
      () => {
        tick.value++;
      },
      { deep: true }
    );

    const totalQty = computed(() => {
      tick.value;
      return controller.sumQuantities();
    });
    const totalAmt = computed(() => {
      tick.value;
      return controller.sumAmounts();
    });
    const qtyWords = computed(() => {
      tick.value;
      return controller.totalQuantityWords();
    });
    const footerMol = computed(() => {
      tick.value;
      return controller.footerMolLine();
    });
    const footerCommission = computed(() => {
      tick.value;
      return controller.footerCommissionLine();
    });

    return {
      form,
      itemsGrid,
      controller,
      settings,
      totalQty,
      totalAmt,
      qtyWords,
      footerMol,
      footerCommission,
    };
  },
});
</script>
